import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { Country, TripWithCountries } from '@travel-planner/shared';
import { useTrips } from './useTrips';

interface TripContextValue {
  trips: TripWithCountries[];
  isLoading: boolean;
  selectedTrip: TripWithCountries | null;
  selectTrip: (id: string) => void;
  countries: Country[];
  selectedCountry: Country | null;
  selectCountry: (id: string) => void;
}

const TripContext = createContext<TripContextValue | null>(null);

const TRIP_KEY = 'tp_trip';
const COUNTRY_KEY = 'tp_country';

export function TripProvider({ children }: { children: ReactNode }) {
  const { data: trips = [], isLoading } = useTrips();
  const [tripId, setTripId] = useState<string | null>(() => localStorage.getItem(TRIP_KEY));
  const [countryId, setCountryId] = useState<string | null>(() =>
    localStorage.getItem(COUNTRY_KEY),
  );

  // Resolve selection against live data, falling back to the first available.
  const selectedTrip = trips.find((t) => t.id === tripId) ?? trips[0] ?? null;
  const countries = selectedTrip?.countries ?? [];
  const selectedCountry = countries.find((c) => c.id === countryId) ?? countries[0] ?? null;

  useEffect(() => {
    if (selectedTrip && selectedTrip.id !== tripId) setTripId(selectedTrip.id);
  }, [selectedTrip, tripId]);

  useEffect(() => {
    if (selectedCountry && selectedCountry.id !== countryId) setCountryId(selectedCountry.id);
  }, [selectedCountry, countryId]);

  useEffect(() => {
    if (tripId) localStorage.setItem(TRIP_KEY, tripId);
  }, [tripId]);

  useEffect(() => {
    if (countryId) localStorage.setItem(COUNTRY_KEY, countryId);
  }, [countryId]);

  const value: TripContextValue = {
    trips,
    isLoading,
    selectedTrip,
    selectTrip: setTripId,
    countries,
    selectedCountry,
    selectCountry: setCountryId,
  };

  return <TripContext.Provider value={value}>{children}</TripContext.Provider>;
}

export function useTripContext(): TripContextValue {
  const ctx = useContext(TripContext);
  if (!ctx) throw new Error('useTripContext must be used within a TripProvider');
  return ctx;
}
